'use client';

import { ReactElement } from 'react';
import { ChevronDownIcon } from '@heroicons/react/16/solid';

import { Text } from '@northladder/i18n';
import { TUIError } from '@northladder/utilities';

import {
  DropdownTw as Dropdown,
  DropdownButtonTw as DropdownButton,
  DropdownItemTw as DropdownItem,
  DropdownMenuTw as DropdownMenu,
} from '../../../ui/dropdown';
import { CircularProgressLoader } from '../../loaders';
import { THeroIcon } from '../../../types';

interface IDropdownItemBase {
  icon?: THeroIcon;
  name: string;
  type: 'action' | 'link';
}

interface IDropdownAction extends IDropdownItemBase {
  onClick: () => void;
  type: 'action';
}

interface IDropdownLink extends IDropdownItemBase {
  href: string;
  type: 'link';
}

type TDropdownItemOptions = IDropdownAction | IDropdownLink;

interface ISimpleDropdownItemProps {
  item: TDropdownItemOptions;
}

/**
 * -----------------------------------------------------------------------------
 * Renders as single item of the dropdown element, choosing whether to display
 * as a link or a button based on the `type`.
 */
function SimpleDropdownItem({ item }: ISimpleDropdownItemProps) {
  const { name, icon: Icon } = item;

  const handleOnClick = () => {
    if (item.type === 'action') {
      item.onClick();
    }
  };

  return (
    <DropdownItem
      className="space-x-2"
      href={item.type === 'link' ? item.href : undefined}
      onClick={handleOnClick}
    >
      {Icon ? <Icon /> : null}
      {name}
    </DropdownItem>
  );
}

interface ISimpleDropdownProps {
  ariaLabel: string;
  /**
   * This is passed to the underlying `DropdownButton` trigger component
   */
  className?: string;
  /**
   * Error in the api call.
   */
  error: TUIError;
  isLoading?: boolean;
  /**
   * for disable the dropdown
   */
  isEnabled?: boolean;
  items: TDropdownItemOptions[];
  /**
   * Used in cases when the trigger is a simple label and no need for a custom element.
   */
  label?: string;
  /**
   * Allows for a custom trigger element to be passed in.
   */
  Trigger?: ReactElement;
}

/**
 * -----------------------------------------------------------------------------
 * This renders a simple dropdown element to show a list of actions that can be
 * performed on a record.
 *
 * - Built upon the TW Catalyst Dropdown - https://catalyst.tailwindui.com/docs/dropdown
 * - Unlike the shad-cn https://ui.shadcn.com/docs/components/dropdown-menu
 *
 * ---
 * - NOTE: If you want to add a more advance dropdown, please create a custom one
 * using the base building blocks used here.
 * - NOTE: Delete the `./simple-dropdown/SimpleDropdown.tsx` once this is stable.
 */
export function SimpleDropdown({
  ariaLabel,
  className,
  error,
  label,
  isEnabled,
  isLoading,
  items,
  Trigger,
}: ISimpleDropdownProps) {
  const Icon = isLoading ? CircularProgressLoader : ChevronDownIcon;

  return (
    <Dropdown>
      <DropdownButton className={className} disabled={!isEnabled} outline>
        <span className="sr-only">{ariaLabel}</span>
        {Trigger || (
          <>
            {label || null}
            <Icon aria-hidden="true" />
          </>
        )}
      </DropdownButton>

      {error ? (
        <div>
          <Text text={error.message} />
          {error.description ? <Text text={error.description} /> : null}
        </div>
      ) : (
        <DropdownMenu>
          {items.map((item) => (
            <SimpleDropdownItem key={item.name} item={item} />
          ))}
          <DropdownItem href="/users/1">View</DropdownItem>
          <DropdownItem href="/users/1/edit">Edit</DropdownItem>
          <DropdownItem>Delete</DropdownItem>
        </DropdownMenu>
      )}
    </Dropdown>
  );
}
